import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import Plot from "react-plotly.js";
import { analyticsService } from "../../services/api";
import '../../styles/components/features/analytics.css';

export default function CourseAnalytics() {
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState("");
    const [courseMetrics, setCourseMetrics] = useState(null);
    const measureRef = useRef(null);
    const [selectWidth, setSelectWidth] = useState("auto");

    const fetchCourses = async () => {
        try {
            const courses = await analyticsService.getCourses();
            setCourses(courses);
        } catch (error) {
            console.error("Error fetching courses:", error);
        }
    };

    const fetchCourseId = async (courseName) => {
        try {
            const courseData = await analyticsService.getCourseId(courseName);
            return courseData.CRN;
        } catch (error) {
            console.error("Error fetching course ID:", error);
        }
    };

    const fetchErrorsPerCourse = async (courseId) => {
        try {
            const courseMetrics = await analyticsService.errorsPerCourse(courseId);
            setCourseMetrics(courseMetrics);
            console.log(courseMetrics)
        } catch (error) {
            console.error("Error fetching errors per course:", error);
        }
    };

    const handleCourseSelection = async (e) => {
        const courseName = e.target.value;
        setSelectedCourse(courseName);
        console.log("Selected Course Name:", courseName);

        try {
            const courseId = await fetchCourseId(courseName);
            if (courseId) {
                console.log("Fetched Course ID:", courseId); // Log the fetched course ID
                await fetchErrorsPerCourse(courseId); // Pass the course ID to fetchErrorsPerCourse
                console.log("Course ID passed to fetchErrorsPerCourse:", courseId); // Log the course ID passed
            } else {
                console.error(`Course ID not found for ${courseName}`);
                alert(`Error fetching course ID for ${courseName}`);
            }
        } catch (error) {
            console.error("Error handling course selection:", error);
            alert(`Error fetching course analytics for ${courseName}`);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    // Measure the longest course name to size the select accordingly
    useLayoutEffect(() => {
        if (!courses || courses.length === 0) {
            setSelectWidth("auto");
            return;
        }
        const longest = courses.reduce((a, b) => (String(a).length >= String(b).length ? a : b), "");
        if (measureRef.current) {
            measureRef.current.textContent = longest || "";
            const base = measureRef.current.offsetWidth || 0;
            const extra = 48; // caret + padding buffer
            setSelectWidth(`${base + extra}px`);
        }
    }, [courses]);

    return (
        <Container className="analytics-container">
            <div className="analytics-title">
                <h1>Real-Time Course Analytics Dashboard</h1>
            </div>
            <Form.Group controlId="courseSelect" className="analytics-form-group">
                <Form.Label className="analytics-label analytics-label-lg">Select a Course</Form.Label>
                <span ref={measureRef} className="analytics-measure" />
                <Form.Select
                    className="analytics-select"
                    style={{ width: selectWidth }}
                    value={selectedCourse}
                    onChange={handleCourseSelection}
                >
                    <option value="">-- Select a Course --</option>
                    {courses.map((courseName, index) => (
                        <option key={index} value={courseName}>
                            {courseName}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            {courseMetrics && (
                <>
                    <Row className="mb-4">
                        <Col md={4} className="text-center">
                            <h5>Total Errors</h5>
                            <p>{courseMetrics[0].error_count ?? "N/A"}</p>
                        </Col>
                        <Col md={4} className="text-center">
                            <h5>Most Common Error Type</h5>
                            <p>
                                {courseMetrics.length > 0
                                    ? Object.entries(
                                        courseMetrics.reduce((acc, curr) => {
                                            acc[curr.errorType] = (acc[curr.errorType] || 0) + curr.error_count;
                                            return acc;
                                        }, {})
                                    ).reduce((mostCommon, [key, value]) => {
                                        return value > mostCommon.count ? { type: key, count: value } : mostCommon;
                                    }, { type: "N/A", count: 0 }).type
                                    : "N/A"}
                            </p>
                        </Col>
                        <Col md={4} className="text-center">
                            <h5>Average Errors Per Student</h5>
                            <p>
                                {courseMetrics.length > 0
                                    ? (courseMetrics.reduce((sum, curr) => sum + curr.error_count, 0) / courseMetrics.length).toFixed(2)
                                    : "N/A"}
                            </p>
                        </Col>
                    </Row>

                    <h3 className="text-center mb-4">Errors Per Topic</h3>
                    <div className="text-center">
                    <Plot
                        data={[
                                {
                                    x: courseMetrics.map((error) => error.errorType), 
                                    y: courseMetrics.map((error) => error.error_count), 
                                    type: "bar",
                                },
                            ]}
                            layout={{
                                title: "Errors Per Topic",
                                xaxis: { title: "Error Type" },
                                yaxis: { title: "Error Count" },
                            }}
                        />
                    </div>
                </>
            )}
        </Container>
    );
}